/**
 * Backbone view for a form which uses file input for uploading user photos via ajax.
 * This view is reusable but makes some assumptions about the class names used in the form (see events object and initialize).
 * File validation rules are hard coded. Refactor to allow them as options passed in if this needs to change.
 *
 * The model passed into this view should handle the validation for non file-input fields.
 * Model url will be used for ajax call made in this file (not using model.save as we use custom ajax settings for multipart form)
 *
 * Options to be passed in e.g. new FormFileUpload({el: 'form', model: new Model()});
 *
 * - Required -
 * @param {string|object} el The form element that the backbone view will use for this.$el
 * @param {Object} model Must have the url to post.  For any form fields (aside from the file input)
 *                       this backbone model must define a .validate() method which will be called
 * - Optional -
 * @param {string[]} [sanitize] Array of element selectors to 'sanitize' (strip html) before posting form
 * @param {string} [eventName] On successful form post, the event will be pubsubed e.g. Backbone.trigger(eventName, response)
 * @param {*} [argForCallback] A little hack. Whatever this value is, it will be passed into the pubsub trigger as a second argument after the api response
 * @param {Function} [onSuccess] Optional success callback override the one in this view
 * @param {Function} [onFail] Optional fail callback to override the one in this view
 * @param {Object} [customEvents] Events are hard coded in this view but if you pass some in they will be used as well.
 *
 * Published Events
 * - successful form post - Backbone.trigger('UserPhotoUploadSuccess', response);
 *
 */
const Backbone = require('backbone');
const _ = require('underscore');
const dustjs = require('dustjs');
const _feedImagePreviewDust = require('store/global/pages/account/projects/_feed_image_preview.dust');
var _isOldBrowser = !window.File && !window.FileReader,
	_allowedFileExtensions = ['jpg', 'jpeg', 'png'],
	_allowedFileTypes = ['image/png', 'image/jpeg'],
	_maxPhotosAllowed = 20,
	// in MB
	_maxFileSize = 10;

var FormFileUpload = Backbone.View.extend({
	events: {
		'click .js-add-photo': '_handleAddPhotoClick',
		'change .js-photo-upload': '_handleFileInputChange',
		'click .js-remove-photo': '_handleRemovePhotoClick',
		'click .js-build-expert': '_handleAskExpertClick',
		submit: '_handleFormSubmitEvent'
	},

	initialize: function(options) {
		this.options = Object.assign({}, options);
		this.$fileInput = this.$('input[type=file]');
		this.$filePreviewWrap = this.$('.file-preview');
		this.$saveButton = this.$('.js-save-button');

		if (options.customEvents) {
			Object.assign(this.events, options.customEvents);
		}

		// for modern browsers we can allow adding/removing files before upload so we store the File object
		// of all the files picked by the file input. They can be removed individually as well. This array
		// will be appended to the FormData object posted via ajax.
		this._files = [];
	},

	_handleFormSubmitEvent: function(e) {
		e.preventDefault();
		if (!this.pendingRequest) {
			this._submitForm();
		}
	},

	_handleAddPhotoClick: function(e) {
		e.preventDefault();
		this.$fileInput.trigger('click');
	},

	_handleRemovePhotoClick: function(e) {
		e.preventDefault();
		var $removeLink = $(e.currentTarget),
			refId = $removeLink.data('refid'),
			$elToRemove = $removeLink.parent();
		this._resetFileInput(refId, $elToRemove);
	},

	_handleFileInputChange: function(e) {
		var fileInput = e.currentTarget;
		if (fileInput.value || fileInput.files) {
			this._imagePreviewForFileInput(fileInput);
			this._showSaveButton();
		} else {
			this._hideSaveButton();
		}
	},

	_showSaveButton() {
		this.$saveButton.show();
	},

	_hideSaveButton() {
		this.$saveButton.hide();
	},

	/**
	 * A one off use case for project/events comments. Can ignore if your form doesn't have '.js-build-expert' element
	 */
	_handleAskExpertClick: function(e) {
		e.preventDefault();
		var $textarea = this.$('textarea'),
			currentVal = $.trim($textarea.val()),
			newVal = `@BuildExpert - ${currentVal}`;
		$textarea.val(newVal);
	},

	/**
     Check for file input length or value
     */
	_hasFiles: function(input) {
		return Boolean(input && (input.value || input.length));
	},

	/**
	 * Handles form submit for both project post and event comment.
	 * Both are the same except the hidden input which either has an eventId (for comment) or a projectId (for post)
	 */
	_submitForm: function() {
		var $form = this.$el,
			fileInput = this.$fileInput[0],
			url = this.model.url(),
			hasFiles = this._hasFiles(fileInput);

		this._sanitizeForm();

		var data = this.serializeForm($form);
		this.model.set(data);
		this.model.set('hasFiles', hasFiles);

		// message not requried if there are photos to upload
		if (url && this.model.isValid()) {
			// so we can prevent double submitting
			this.pendingRequest = true;

			if (this.options.loadingClass) {
				this.$el.addClass(this.options.loadingClass);
			}

			if (!_isOldBrowser) {
				// since we're storing files in this._files, remove any files hanging around in the file input
				this._resetFileInput();
				this._deleteFileInputForSafari();
				var formData = new FormData($form[0]);

				this._files.forEach((f) => {
					formData.append('photos', f, f.name);
				});

				this._makeFormPost(formData, url, this._postSuccessCallback.bind(this), this._ajaxFailCallback.bind(this));
			} else {
				this._makeIframeFormPost($form, url, this._postSuccessCallback.bind(this), this._ajaxFailCallback.bind(this));
			}
		}
	},

	/**
	 * Temprory fix a bug in Safari current version 11.1.2
	 * This bug will cause an error when passing an empty input with type = 'file' to FormData
	 * Node server will not be able to parse the form data in the back end
	 * Remove this method when bug is fixed
	 */
	_deleteFileInputForSafari: function() {
		const $formInput = this.$('input:file');

		if ($formInput[0] && $formInput[0].files && $formInput[0].files.length === 0) {
			$formInput.remove();
		}
	},

	/**
	 * Callback for a successful ajax project post. Will reset the form.
	 * @param  {Object} response
	 */
	_postSuccessCallback: function(response) {
		if (this.options.loadingClass) {
			this.$el.removeClass(this.options.loadingClass);
		}

		if (response && response.status === 0) {
			return this._ajaxFailCallback(response);
		}
		// running the custom on success callback
		if (typeof this.options.onSuccess === 'function') {
			return this.options.onSuccess(response, this.options.argForCallback);
		}

		Backbone.trigger(this.options.eventName || 'UserPhotoUploadSuccess', response, this.options.argForCallback);
		this._resetForm();
		this._hideSaveButton();
	},

	/**
	 * Resets the form element and clears out any files that were uploaded
	 */
	_resetForm: function() {
		this.el.reset();
		this._files = [];
		this.$filePreviewWrap.empty();
		// allow form submission again
		this.pendingRequest = false;
	},

	/**
	 * Callback for a failed ajax. Will check for optional fail callback and run that if it exists.
	 * @param  {Object} response
	 */
	_ajaxFailCallback: function(response) {
		if (this.options.loadingClass) {
			this.$el.removeClass(this.options.loadingClass);
		}

		if (typeof this.options.onFail === 'function') {
			this.pendingRequest = false;
			return this.options.onFail(response);
		}

		var customErrorMsg = response && response.error ? response.error : null;
		var msg = customErrorMsg && customErrorMsg.length ? customErrorMsg : 'Sorry! We had a problem.';
		window.BCOM.notifications.render(msg, 'danger');
		// allow form submission again
		this.pendingRequest = false;
	},

	/**
	 * Makes ajax call to activity api. These settings apparently allow jquery to make a multipart request.
	 * @param  {Object} formData Data to post
	 * @param  {string} url Event comments and project Posts have different urls
	 * @param  {Function} successCB Ajax success callback
	 * @param  {Function} failCB Ajax fail callback
	 */
	_makeFormPost: function(formData, url, successCB, failCB) {
		var method = this.model.id ? 'PUT' : 'POST';
		return $.ajax({
			url: url,
			type: method,
			data: formData,
			contentType: false,
			processData: false
		}).then(successCB, failCB);
	},

	/**
	 * Reads a file and fires a callback
	 * @param  {Object} file
	 * @param  {Function} successCB
	 * @param  {Function} failCB
	 */
	_readFile: function(file, successCB, failCB) {
		var reader = new window.FileReader();
		reader.onload = successCB;
		reader.onerror = failCB;
		reader.readAsDataURL(file);
	},

	/**
	 * Does a modern browser file preview using FileReader Api
	 * @param  {Object}   fileInput DOM element
	 * @param  {Function} callback  Callback pass the array of photos
	 */
	_imagePreviewModern: function(fileInput, callback) {
		var self = this,
			dfds = [],
			photos = [];

		if (this._files.length + fileInput.files.length > _maxPhotosAllowed) {
			this._resetFileInput();
			return window.BCOM.notifications.render(`You may upload a max of ${_maxPhotosAllowed} photos.`, 'danger');
		}

		if (fileInput.files) {
			for (let i = 0; i < fileInput.files.length; i++) {
				const dfd = $.Deferred(),
					file = fileInput.files[i];
				(function(dfd, file) {
					dfds.push(dfd);
					const successCB = function(e) {
						// refId used for user removing later...
						var refId = _.random(100, 100000);
						photos.push({
							src: e.target.result,
							name: file.name,
							refId: refId,
							size: file.size,
							type: file.type
						});
						file.refId = refId;
						self._files.push(file);
						dfd.resolve();
					};
					self._readFile(file, successCB, dfd.reject);
				})(dfd, file);
			}
		}

		if (dfds.length) {
			$.when.apply($, dfds).done(function() {
				callback(photos);
			});
		} else {
			callback([]);
		}
	},

	/**
	 * Grabs file input and attempts to create a preview
	 * @param  {Object} fileInput DOM element
	 */
	_imagePreviewForFileInput: function(fileInput) {
		if (!fileInput || !fileInput.value) {
			return false;
		}

		if (_isOldBrowser) {
			this._buildImagePreview({
				// get file name (strips out any path stuff)
				name: fileInput.value.replace(/.*(\/|\\)/, '')
			});
		} else {
			this._imagePreviewModern(fileInput, this._buildImagePreview.bind(this));
		}
	},

	/**
	 * Will build the dust image preview template. Files are validated and potential errors displayed in dust template.
	 * @param  {Array} photos Array of photo objects
	 */
	_buildImagePreview: function(photos) {
		var self = this;
		self._validateFiles(photos);
		dustjs.render(
			_feedImagePreviewDust,
			{
				photos: photos
			},
			function(err, out) {
				self.$filePreviewWrap[_isOldBrowser ? 'html' : 'append'](out);
			}
		);
	},

	/**
	 * Validates the file and places the error message on the file object for users of this method.
	 * More robust validation will be performed in modern browsers.
	 * @param  {Array} files Array of files to validate
	 */
	_validateFiles: function(files) {
		var self = this;
		files.forEach((file) => {
			if (!_isOldBrowser) {
				var sizeMB = file.size / 1024 / 1024;
				if (sizeMB > _maxFileSize) {
					file.error = `Max photo size is ${_maxFileSize}MB`;
				}
				if (!_.contains(_allowedFileTypes, file.type)) {
					file.error = `Please use: ${_allowedFileTypes.join(', ')}.`;
				}
			}
			var ext = file.name.replace(/.*[.]/, '').toLowerCase();
			if (!_.contains(_allowedFileExtensions, ext)) {
				file.error = `Please use: ${_allowedFileExtensions.join(', ')}.`;
			}
			// make sure it doesn't make it into the actual form post
			if (file.error) {
				self._resetFileInput(file.refId);
			}
		});
	},

	/**
	 * Cleans value in the passed in text input/textarea.
	 * @param  {Object}  $input
	 */
	_sanitizeInput: function($input) {
		var origText = $.trim($input.val()) || '';
		var newText = $.trim(window.BCOM.util.sanitize.stripHtmlTags(origText));
		$input.val(newText);
	},

	/**
	 * Loops through options.sanitize selectors and calls _sanitizeInput for each.
	 */
	_sanitizeForm: function() {
		var self = this;
		if (Array.isArray(this.options.sanitize)) {
			this.options.sanitize.forEach((selector) => {
				self._sanitizeInput(self.$(selector));
			});
		}
	},

	/**
	 * Will reset the file input for the form.
	 * @param  {number} [refId]  The internal reference to the file object (new browsers only)
	 * @param  {Object} [$elToRemove] The wrapper of the image preview
	 */
	_resetFileInput: function(refId, $elToRemove) {
		if ($elToRemove && $elToRemove.length) {
			$elToRemove.remove();
		}
		// need to remove file value from input
		this.$fileInput = this.$fileInput.replaceWith(this.$fileInput.val('').clone(true));
		this.$fileInput.off('change').on('change', this._handleFileInputChange.bind(this));

		// if modern browser we are also storing the File locally for later posting via formData
		if (!_isOldBrowser && refId) {
			this._files = this._files.filter((file) => file.refId !== refId);
		}

		if (!this.$filePreviewWrap.children().length) {
			this._hideSaveButton();
		}
	},

	// ************************************** IFRAME STUFF FOR OLD BROWSER AJAX FILE UPLOADS ******************************

	/**
	 * Post form to iframe for browsers that don't support FormData
	 * @param  {Object} $form The jquery form element
	 * @param  {string} url Event comments and project Posts have different urls
	 * @param  {Function} successCB Ajax success callback
	 * @param  {Function} failCB Ajax fail callback
	 */
	_makeIframeFormPost: function($form, url, successCB, failCB) {
		var self = this,
			$iframe = $('<iframe id="iframeUpload" name="iframeUpload" class="hide"></iframe>').appendTo('body');

		// set up new form attributes so we can post to iframe
		// remove 'submit' event so it doesn't run through _handleFormSubmitEvent again
		$form
			.attr({
				target: 'iframeUpload',
				action: url
			})
			.off('submit');

		// handle iframe load event and call success or fail callbacks as needed
		$iframe.one('load', function() {
			var response = self._parseIframeContent($iframe);
			self._handleIframeResponse(response, successCB, failCB);
			// rebind to _handleFormSubmitEvent
			$form.on('submit', self._handleFormSubmitEvent.bind(self));
			$iframe.remove();
		});
		// re-submit form to iframe
		$form.trigger('submit');
	},

	/**
	 * Parses the api response inside the iframe
	 * @param  {Object} $iframe jquery iframe object
	 */
	_parseIframeContent: function($iframe) {
		var response;
		try {
			response = $.parseJSON(
				$iframe
					.contents()
					.find('body pre')
					.html()
			);
		} catch (e) {
			response = null;
		}
		return response;
	},

	/**
	 * Takes the parsed iframe response and handles it, calling appropriate callbacks
	 * @param  {Object} response
	 * @param  {Function} successCB
	 * @param  {Function} failCB
	 */
	_handleIframeResponse: function(response, successCB, failCB) {
		// successfull response will have an id for the event just posted
		if (!response || response.status === 0) {
			return failCB(response);
		}
		successCB(response);
	}
});

module.exports = FormFileUpload;
